import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { validateTemplate } from "@repo/print-pdf/arbre/print/template";
import type { PrintTemplate } from "@repo/types";
import {
  useEffectivePrintTemplate,
  useResetPrintTemplate,
  useSavePrintTemplate,
} from "@repo/ui/hooks/api/print-templates";
import { Button } from "@repo/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/ui/card";
import { Switch } from "@repo/ui/components/ui/switch";
import {
  Download,
  GripVertical,
  Redo2,
  RotateCcw,
  Save,
  Undo2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ColorField } from "./color-field";
import { FitPreview } from "./fit-preview";
import {
  EDITOR_VARIANT_KEYS,
  EDITOR_VARIANTS,
  type EditorVariant,
  type EditorVariantKey,
} from "./editor-variants";
import { frenchAnnouncements, SCREEN_READER_INSTRUCTIONS } from "./dnd-helpers";
import { SectionEditor, sectionName } from "./section-editor";
import { parseServerTemplate } from "./template-source";
import { useTemplateDraft } from "./use-template-draft";

const HEADER_OPTIONS = [
  { key: "showLogo", label: "Logo" },
  { key: "showOrganisation", label: "Organisation" },
  { key: "showSiret", label: "SIRET" },
  { key: "showRecenseur", label: "Recenseur" },
] as const;

/** Génère le PDF d'exemple avec le brouillon courant. react-pdf n'est chargé qu'au premier clic (bibliothèque volumineuse). */
async function downloadPreviewPdf(
  variant: EditorVariant,
  template: PrintTemplate,
) {
  const [{ Document, pdf }, buildPages] = await Promise.all([
    import("@react-pdf/renderer"),
    variant.loadPdfPages(),
  ]);
  const blob = await pdf(<Document>{buildPages(template)}</Document>).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = variant.pdfFilename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Charge le modèle de l'organisation puis ouvre l'éditeur. L'éditeur est recréé à chaque nouvelle révision (après un enregistrement ou un rechargement). */
export function PrintTemplatesPage() {
  const [variantKey, setVariantKey] = useState<EditorVariantKey>("ARBRE");
  const variant = EDITOR_VARIANTS[variantKey];
  const query = useEffectivePrintTemplate(variantKey);

  const tabs = (
    <div className="flex flex-wrap gap-2 px-4 pt-4" role="tablist">
      {EDITOR_VARIANT_KEYS.map((key) => (
        <Button
          key={key}
          type="button"
          role="tab"
          aria-selected={key === variantKey}
          variant={key === variantKey ? "default" : "outline"}
          onClick={() => setVariantKey(key)}
        >
          {EDITOR_VARIANTS[key].label}
        </Button>
      ))}
    </div>
  );

  if (query.isLoading)
    return (
      <>
        {tabs}
        <p className="p-4 text-muted-foreground">Chargement du modèle…</p>
      </>
    );
  if (query.isError || !query.data)
    return (
      <>
        {tabs}
        <p className="p-4 text-destructive" role="alert">
          Impossible de charger le modèle de fiche.
        </p>
      </>
    );
  const { config, revision } = query.data;
  return (
    <>
      {tabs}
      <TemplateEditor
        key={`${variantKey}-${revision}`}
        variantKey={variantKey}
        variant={variant}
        saved={
          parseServerTemplate(config, variantKey) ?? variant.defaultTemplate
        }
        revision={revision}
        hasCustomTemplate={config !== null}
        onReload={() => void query.refetch()}
      />
    </>
  );
}

function TemplateEditor({
  variantKey,
  variant,
  saved,
  revision,
  hasCustomTemplate,
  onReload,
}: {
  variantKey: EditorVariantKey;
  variant: EditorVariant;
  saved: PrintTemplate;
  revision: number;
  hasCustomTemplate: boolean;
  onReload: () => void;
}) {
  const [pdfState, setPdfState] = useState<"idle" | "busy" | "error">("idle");
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const { draft, dispatch, canUndo, canRedo, hasUnsavedChanges } =
    useTemplateDraft(saved);
  const save = useSavePrintTemplate(variantKey);
  const reset = useResetPrintTemplate(variantKey);

  // Prévient avant de quitter l'onglet avec des modifications non enregistrées.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedChanges]);

  const onSave = () =>
    save.mutate(
      { config: draft, expectedRevision: revision },
      {
        onSuccess: () => toast.success("Modèle enregistré pour l'organisation"),
        onError: (error) => {
          if (
            (error as { response?: { status?: number } }).response?.status ===
            409
          )
            setConflict(true);
          else toast.error("Enregistrement impossible, réessayez.");
        },
      },
    );

  const onResetToDefault = () => {
    if (
      !window.confirm(
        "Revenir à la fiche par défaut pour toute l'organisation ?",
      )
    )
      return;
    reset.mutate(undefined, {
      onSuccess: () => toast.success("Fiche par défaut rétablie"),
      onError: () => toast.error("Impossible de rétablir la fiche par défaut."),
    });
  };
  const fieldByKey = useMemo(
    () => new Map(variant.fields.map((f) => [f.key, f])),
    [variant],
  );
  const sectionDefs = useMemo(
    () => new Map(variant.sections.map((s) => [s.id, s])),
    [variant],
  );
  const errors = useMemo(
    () => validateTemplate(draft, variant.sections, variant.fields),
    [draft, variant],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Ctrl/Cmd+Z annule, Ctrl/Cmd+Maj+Z ou Ctrl+Y rétablit (hors champs de saisie, qui gardent leur propre annulation).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']"))
        return;
      const key = event.key.toLowerCase();
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        dispatch({ type: "undo" });
      } else if (key === "y" || (key === "z" && event.shiftKey)) {
        event.preventDefault();
        dispatch({ type: "redo" });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dispatch]);

  const sectionLabel = (id: string) => {
    const def = sectionDefs.get(id);
    return def ? sectionName(def) : id;
  };

  const onDragStart = ({ active }: DragStartEvent) =>
    setActiveSection(String(active.id));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveSection(null);
    if (over && active.id !== over.id)
      dispatch({
        type: "moveSection",
        activeId: String(active.id),
        overId: String(over.id),
      });
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
            Modèles de fiche
            {hasUnsavedChanges && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Modifications non enregistrées
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {variant.description} : choisissez les sections et les champs
            affichés, leur ordre et leur colonne. Le modèle enregistré
            s'applique aux fiches et aux impressions multiples de toute
            l'organisation.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => dispatch({ type: "undo" })}
            disabled={!canUndo}
            aria-label="Annuler (Ctrl+Z)"
            title="Annuler (Ctrl+Z)"
          >
            <Undo2 className="mr-2 h-4 w-4" />
            Annuler
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => dispatch({ type: "redo" })}
            disabled={!canRedo}
            aria-label="Rétablir (Ctrl+Y)"
            title="Rétablir (Ctrl+Y)"
          >
            <Redo2 className="mr-2 h-4 w-4" />
            Rétablir
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pdfState === "busy"}
            onClick={async () => {
              setPdfState("busy");
              try {
                await downloadPreviewPdf(variant, draft);
                setPdfState("idle");
              } catch {
                setPdfState("error");
              }
            }}
          >
            <Download className="mr-2 h-4 w-4" />
            {pdfState === "busy" ? "Génération…" : "Aperçu PDF"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onResetToDefault}
            disabled={!hasCustomTemplate || reset.isPending}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Rétablir le modèle par défaut
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={!hasUnsavedChanges || errors.length > 0 || save.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {save.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>
      {conflict && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          role="alert"
        >
          Ce modèle a été modifié par quelqu'un d'autre depuis son ouverture.
          Rechargez-le pour voir ses changements (vos modifications non
          enregistrées seront perdues).
          <Button type="button" size="sm" variant="outline" onClick={onReload}>
            Recharger
          </Button>
        </div>
      )}
      {pdfState === "error" && (
        <p className="text-sm text-destructive" role="alert">
          Impossible de générer le PDF.
        </p>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,36rem)]">
        <Card className="overflow-hidden xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:self-start xl:overflow-auto">
          <CardHeader>
            <CardTitle className="text-base">
              Aperçu (inventaire d'exemple)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FitPreview>{variant.preview(draft)}</FitPreview>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">En-tête</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              {HEADER_OPTIONS.map((option) => (
                <label
                  key={option.key}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                >
                  {option.label}
                  <Switch
                    checked={draft.header[option.key]}
                    onCheckedChange={() =>
                      dispatch({ type: "toggleHeader", key: option.key })
                    }
                  />
                </label>
              ))}
              <div className="col-span-2 space-y-1 rounded-md bg-muted/40 p-2">
                <ColorField
                  label="Barre du titre"
                  value={draft.header.titleBg}
                  fallback={variant.titleBg}
                  onChange={(value) =>
                    dispatch({ type: "setHeaderColor", key: "titleBg", value })
                  }
                />
                <ColorField
                  label="Texte du titre"
                  value={draft.header.titleColor}
                  fallback="#FFFFFF"
                  onChange={(value) =>
                    dispatch({
                      type: "setHeaderColor",
                      key: "titleColor",
                      value,
                    })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sections</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragCancel={() => setActiveSection(null)}
                accessibility={{
                  announcements: frenchAnnouncements(sectionLabel),
                  screenReaderInstructions: SCREEN_READER_INSTRUCTIONS,
                }}
              >
                <SortableContext
                  items={draft.sections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {draft.sections.map((section) => {
                    const def = sectionDefs.get(section.id);
                    return def ? (
                      <SectionEditor
                        key={section.id}
                        section={section}
                        def={def}
                        fieldByKey={fieldByKey}
                        dispatch={dispatch}
                      />
                    ) : null;
                  })}
                </SortableContext>
                <DragOverlay dropAnimation={{ duration: 150 }}>
                  {activeSection ? (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm font-medium shadow-lg">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      {sectionLabel(activeSection)}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
              {errors.length > 0 && (
                <ul
                  className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
                  role="alert"
                >
                  {errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
